import { InjectionToken } from '@jkhy/frontend-utils/di';

export const INSTITUTION_ID = 'institutionId';
export const INSTITUTION_UNIVERSAL_ID = 'institutionUniversalId';

interface AppMetaData {
    appName: string;
    version: string;
    buildType: 'production' | 'development';
    url: string;
    /**
     * Is `true` if the application is being served by Webpack dev server.
     */
    isLocal: boolean;
}

export enum AppType {
    Unknown = 'Unknown',
    Channel = 'Channel',
    BackOffice = 'BackOffice',
    Pwa = 'PWA',
}

export enum BuildType {
    Unknown = 'unknown',
    Production = 'production',
    Development = 'development',
}

export enum Environment {
    Unknown = 'unknown',
    Local = 'local',
    Dev = 'dev',
    Staging = 'staging',
    Production = 'production',
}

/**
 * Inlined value from Webpack's [`DefinePlugin`](https://webpack.js.org/plugins/define-plugin/).
 */
declare const APP_TYPE: AppType | undefined;
/**
 * Inlined value from Webpack's [`DefinePlugin`](https://webpack.js.org/plugins/define-plugin/).
 */
declare const BUILD_TYPE: BuildType | undefined;

export const AppTypeToken = new InjectionToken('AppType', {
    // specifically use typeof operator to guard against undeclared inlined value
    value: typeof APP_TYPE === 'undefined' ? AppType.Unknown : APP_TYPE,
});

export const BuildTypeToken = new InjectionToken('BuildType', {
    // specifically use typeof operator to guard against undeclared inlined value
    value: typeof BUILD_TYPE === 'undefined' ? BuildType.Unknown : BUILD_TYPE,
});

/**
 * Injected configuration data used for bootstrapping the app, if any.
 */
export interface InjectedConfig {
    /**
     * The root URL used by the channel or BO application to make API requests.
     */
    apiRoot?: string;
    /**
     * The client token used to enable the analytics SDK.
     */
    analyticsToken?: string;
    /**
     * The client token used to enable the error logging SDK.
     */
    loggingToken?: string;
    /**
     * The base path of the application.
     */
    basePath?: string;
    /**
     * Whether the application is authenticated using banno authentication.
     */
    bannoAuthEnabled?: boolean;
    /**
     * Whether the application is running inside a native mobile app wrapper (e.g. iOS WKWebView).
     * Set `window.config.isNativeApp = true` in the native layer before the WebView loads.
     */
    isNativeApp?: boolean;
}

/**
 * Global scope augmented with configuration and metadata information.
 *
 * See `index.html.ejs` amd `webpack.shared.js` for the mechanics of how this data is injected.
 */
export type InjectedWindow = Window & {
    /**
     * Configuration data manually specified or injected into `index.html.ejs`
     * using `HtmlPlugin` [`templateParameters`](https://github.com/jantimon/html-webpack-plugin#options).
     */
    config: InjectedConfig;
    /**
     * App metadata injected into `index.html.ejs` using `HtmlPlugin` [`templateParameters`](https://github.com/jantimon/html-webpack-plugin#options).
     */
    treasury: AppMetaData;
};
